import React, { useCallback, useContext, useMemo } from 'react';

import { useExportTemplate, useStatusTemplates } from 'Templates';

import {
  ActionBar,
  Actions,
  Action,
  Typography,
  MoreVertIcon,
  DownloadIcon,
  DescriptionIcon,
  FileUploadIcon,
} from '@gogame/components';
import { useChildComponent, useChooseFiles } from '@gogame/hooks-ts';
import { FullScreenToggler } from '@gogame/data-grid';
import { useQueryClient } from '@gogame/query';
import { useParams } from '@gogame/screens';
import { useScreenState } from '@gogame/screens';

import CreateCreativesForm from 'scenes/Gallery/GalleryPage/Creatives/CreateCreativesForm';
import CreativesDataTableContext from 'scenes/Gallery/GalleryPage/Creatives/CreativesDataTable/CreativesDataTableContext';
import {
  CREATIVES,
  FILE,
  SOUND_ID,
} from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  useBulkUpload as useBulkUploadCreatives,
  useExportCreatives,
} from 'scenes/Gallery/GalleryPage/Creatives/data';
import OutsourceStudios from 'scenes/Gallery/GalleryPage/OutsourceStudios';
import SearchField from 'scenes/Gallery/components/SearchField/SearchField';
import SwitchViewMode from 'scenes/Gallery/components/SwitchViewMode';
import {
  CONCEPT_ID,
  EXCEL,
  ZIP,
  VIEW_MODE_TILES,
  TEMPLATE_BULK_TEMPLATE,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { getSoundId } from 'scenes/Gallery/GalleryPage/utils/getSoundId';

import OrderControl from './OrderControl';

const Toolbar = ({ withTitle, tabs }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const params = useParams();
  const conceptId = useMemo(() => {
    if (params?.conceptId) {
      return Number(params?.conceptId);
    }
  }, [params?.conceptId]);

  const conceptFamilyId = useMemo(() => {
    if (params?.conceptFamilyId) {
      return Number(params?.conceptFamilyId);
    }
  }, [params?.conceptFamilyId]);

  const queryClient = useQueryClient();

  const { viewMode, setViewMode, data, tableSettings, concept } = useContext(
    CreativesDataTableContext
  );
  const {
    isAllowCreateCreatives,
    isAllowManageOutsourceStudio,
    isAllowUploadPlayable,
  } = useGalleryPermissions();

  const chooseBulkFile = useChooseFiles({
    multiple: false,
    accept: 'application/zip,application/x-zip-compressed,multipart/x-zip',
  });
  const chooseFiles = useChooseFiles({
    multiple: true,
    accept: `image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/quicktime${
      isAllowUploadPlayable
        ? ',application/zip,application/x-zip-compressed,multipart/x-zip'
        : ''
    }`,
  });

  const { filters, searchQuery, searchType } = useScreenState();
  const [bulkUploadCreatives] = useBulkUploadCreatives();

  const [exportBulkTemplate] = useExportTemplate({
    name: TEMPLATE_BULK_TEMPLATE,
  });
  const [exportExcelCreatives] = useExportCreatives({
    type: EXCEL,
  });

  const [exportZipCreatives] = useExportCreatives({
    type: ZIP,
  });

  const handleExportExcelCreatives = useCallback(() => {
    return exportExcelCreatives({
      conceptId: conceptId,
      conceptFamilyId: conceptFamilyId,
      tableSettings,
      filters,
      searchQuery,
      searchType,
    });
  }, [exportExcelCreatives, conceptId, conceptFamilyId]);

  const handleExportZipCreatives = useCallback(() => {
    const size = data.rows.reduce((result, { size = 0 }) => result + size, 0);

    return exportZipCreatives(
      {
        conceptId: conceptId,
        conceptFamilyId: conceptFamilyId,
        tableSettings,
        filters,
        searchQuery,
        searchType,
      },
      { size }
    );
  }, [exportZipCreatives, conceptId, conceptFamilyId, data?.rows]);

  const handleOutsourceStudios = useCallback((id) => {
    openChildComponent(OutsourceStudios, {
      id,
      onChange: () => {
        queryClient.resetQueries({ active: true });
      },
    });
  }, []);

  const handleExportBulkTemplate = async () => exportBulkTemplate();

  const handleBulkUpload = async () => {
    const file = await chooseBulkFile();
    await bulkUploadCreatives({ file, conceptId });
  };
  const handleCreativesUploader = async () => {
    const files = await chooseFiles({ accept: isAllowUploadPlayable });

    openChildComponent(CreateCreativesForm, {
      initialValues: {
        [CONCEPT_ID]: conceptId,
        [CREATIVES]: files.map((file) => {
          const soundId = getSoundId(file?.name);
          const fileObj = { [FILE]: file };
          if (soundId) {
            fileObj[SOUND_ID] = soundId;
          }
          return fileObj;
        }),
      },
    });
  };

  const labelExport = `Export ${conceptId ? 'variants' : 'creatives'}`;

  const rowsCount = data?.rows?.length;

  const templateNewUpdated = useStatusTemplates({
    name: TEMPLATE_BULK_TEMPLATE,
  });

  return (
    <>
      <ActionBar
        search={<SearchField />}
        secondaryActions={
          <Actions>
            {viewMode === VIEW_MODE_TILES && (
              <Action custom={<OrderControl />} />
            )}

            <Action icon={<DownloadIcon />} tooltip={labelExport}>
              <Action
                label={'Export to excel'}
                onClick={handleExportExcelCreatives}
              />
              <Action label={labelExport} onClick={handleExportZipCreatives} />
            </Action>
            {isAllowCreateCreatives(concept) && (
              <Action
                label={'Bulk upload'}
                icon={<FileUploadIcon />}
                onClick={handleBulkUpload}
              />
            )}
            {isAllowCreateCreatives(concept) && (
              <Action
                label={`Download Bulk template${
                  templateNewUpdated ? ' (Updated)' : ''
                }`}
                icon={<DescriptionIcon />}
                badge={templateNewUpdated}
                onClick={handleExportBulkTemplate}
              />
            )}

            <Action component={FullScreenToggler} />
            {isAllowManageOutsourceStudio && (
              <Action icon={<MoreVertIcon />} tooltip={'More'}>
                <Action
                  button
                  label={'Manage Outsource Studios'}
                  onClick={handleOutsourceStudios}
                  color={'primary'}
                />
              </Action>
            )}
          </Actions>
        }
        primaryActions={
          conceptId ? (
            <Actions>
              <Action
                border={true}
                button={true}
                add={true}
                iconContent={true}
                tooltip={'Add Creatives'}
                variant={'outlined'}
                onClick={handleCreativesUploader}
              />
            </Actions>
          ) : null
        }
        modeSwitcher={
          <Actions>
            <Action
              custom={
                <SwitchViewMode viewMode={viewMode} setViewMode={setViewMode} />
              }
            />
          </Actions>
        }
      >
        {withTitle ? (
          <Typography variant={'subtitle1'}>
            Creatives {rowsCount >= 0 ? `(${rowsCount})` : ''}
          </Typography>
        ) : (
          tabs
        )}
      </ActionBar>
      {childComponent}
    </>
  );
};
export default Toolbar;
